import useContent from './useContent';
import useSuspend from '../lib/useSuspend';
import { computed, hasInjectionContext, inject, ref } from 'vue';
import { getCourseId, getFirm, isFirm, get } from '../lib/SCORM';
import { getCustomContent } from '../lib/getContent';

const firm = getFirm();
const courseId = getCourseId();
const audioDocMode = ref(false);

/**
 * Generates an MP3 file name for a given name, in the format `firm-courseId-locale-name.mp3`, for example, `XEY-244002-fr-welcome_title.mp3`.
 *
 * If the locale is `en-GB`, it is omitted. If there is no firm, it is omitted.
 *
 * @param name
 */
export const getAudioFilename = (name: string, locale?: string) => {
  const loc = locale || useSuspend()?.locale || get('locale');
  // include locale if not en-GB
  const localeTerm = loc === 'en-GB' ? null : loc;
  const customContent = getCustomContent();
  const hasCustomAudioPath = Boolean(
    customContent?.course?.audio?.[loc] ||
    customContent?.patches?.[`$course.audio.${loc}`]
  );

  // skip firms like "___" and REVIEW.
  // Don't use the firm if we haven't specified custom audio (we use the base course's audio)
  const firmTerm =
    /^[a-zA-Z]/.test(firm) &&
    !isFirm('REVIEW') &&
    (hasCustomAudioPath || audioDocMode.value)
      ? firm.toUpperCase()
      : null;

  const filename =
    [firmTerm, courseId, localeTerm, name].filter(Boolean).join('-') + '.mp3';

  // console.log('getAudioFilename', hasCustomAudioPath, audioDoc, filename);

  return filename;
};

/**
 * Returns methods for getting audio file URLs and names.
 */
export default function useAudio() {
  const suspend = useSuspend();
  const content = useContent();
  // `suspend` is undefined when this composable is instantiated outside an
  // injection context (e.g. the split/carousel/slides audio resolvers run from
  // a watcher or post-await lifecycle). Fall back to the SCORM-persisted locale
  // before 'en-GB' — matching useAudioNarration — so non-en-GB courses don't
  // wrongly resolve English S3 audio.
  const locale = computed(() => suspend?.locale ?? get('locale') ?? 'en-GB');
  const folder = computed(
    () => content.compiled?.course?.audio?.[locale.value]
  );
  const folderWithTrailingSlash = computed(() =>
    folder.value?.endsWith('/') ? folder.value : folder.value + '/'
  );

  if (hasInjectionContext()) {
    audioDocMode.value = inject('audioDoc', false) as boolean;
  }

  return {
    getAudioURL(name?: string) {
      if (!name) return null;

      const filename = getAudioFilename(name, locale.value);

      return folder.value
        ? `${folderWithTrailingSlash.value}${filename}`
        : null;
    },

    getAudioFilename(name?: string) {
      if (!name) return null;
      return getAudioFilename(name, locale.value);
    }
  };
}
